// @internal
import React, { useContext } from 'react';
import { SideNavContext } from '../../SideNavContext';
import Box from '../../../Box/Box';
import Bubble, { BubbleProps } from '../../../Bubble/Bubble';
import SideNavIcon from '../SideNavIcon/SideNavIcon';
import { IconProps } from '../../../Icon/Icon';
import {
  StyledSideNavBubbleWrapper,
  StyledSideNavItemAnchor,
  StyledSideNavItemLabel,
  StyledSideNavNewTabIcon,
  StyledSideNavItemLink,
} from './styles';
import { SideNavItemProps } from './SideNavItem';

type SideNavItemContentProps = {
  title: string;
  isClosed: boolean;
  imageIcon: IconProps['type'];
  bubble?: { type?: BubbleProps['type']; text: string | number } | null;
  openInNewTab?: boolean;
  'data-testid'?: string | number;
};

const InternalSideNavItemContent = (props: SideNavItemContentProps) => {
  const { title, isClosed, imageIcon, bubble, openInNewTab, 'data-testid': dataTestId } = props;

  return (
    <>
      <Box as="span" display="flex" alignItems="center">
        {imageIcon && <SideNavIcon icon={imageIcon} isBadged={!!bubble && isClosed} meaning={title} />}
        <StyledSideNavItemLabel data-testid={dataTestId ? `${dataTestId}-content-label` : ''} isClosed={isClosed}>
          {title}
        </StyledSideNavItemLabel>
      </Box>
      {bubble && Number(bubble.text) !== 0 && (
        <StyledSideNavBubbleWrapper isClosed={isClosed} data-testid={dataTestId ? `${dataTestId}-content-bubble` : ''}>
          <Bubble type={bubble.type || 'orange'}>{bubble.text}</Bubble>
        </StyledSideNavBubbleWrapper>
      )}
      {openInNewTab && <StyledSideNavNewTabIcon />}
    </>
  );
};

type ReactRouterSideNavLinkProps = SideNavItemProps & {
  isClosed: boolean;
};

const ReactRouterSideNavLink = React.forwardRef<HTMLAnchorElement, ReactRouterSideNavLinkProps>(
  ({ 'data-testid': dataTestId, isActive, url, openInNewTab, imageIcon, bubble, ...otherProps }, ref) => (
    <StyledSideNavItemLink
      innerRef={ref}
      isActive={isActive}
      data-testid={dataTestId ? `${dataTestId}-router-link${isActive ? '--active' : ''}` : ''}
      to={url}
      target={openInNewTab ? '__blank' : undefined}
      rel={openInNewTab ? 'noreferrer noopener' : undefined}
      {...otherProps}
    />
  ),
);

const AnchorSideNavLink = React.forwardRef<HTMLAnchorElement, ReactRouterSideNavLinkProps>(
  ({ 'data-testid': dataTestId, url, isActive, openInNewTab, ...otherProps }, ref) => (
    <StyledSideNavItemAnchor
      ref={ref}
      data-testid={dataTestId ? `${dataTestId}-anchor${isActive ? '--active' : ''}` : ''}
      href={url}
      isActive={isActive}
      target={openInNewTab ? '__blank' : undefined}
      rel={openInNewTab ? 'noreferrer noopener' : undefined}
      {...otherProps}
    />
  ),
);

export const SideNavItemContent = React.forwardRef<HTMLAnchorElement, SideNavItemProps>((props, ref) => {
  const { isActive = false, withReactRouter, ...otherProps } = props;

  const { isClosed } = useContext(SideNavContext);

  const SideNavLink = React.memo(withReactRouter ? ReactRouterSideNavLink : AnchorSideNavLink);

  return (
    <Box display="flex">
      <SideNavLink ref={ref} isClosed={isClosed} isActive={isActive} {...otherProps}>
        <InternalSideNavItemContent
          isClosed={isClosed}
          title={props.title}
          imageIcon={props.imageIcon}
          bubble={props.bubble}
          openInNewTab={props.openInNewTab}
          data-testid={props['data-testid']}
        />
      </SideNavLink>
    </Box>
  );
});
